(() => {
  const ELEMENT_NAME = 'unilar-announcement-bar';
  const MAX_TIMEOUT = 2147483647;

  if (!('customElements' in window) || customElements.get(ELEMENT_NAME)) return;

  class UnilarAnnouncementBar extends HTMLElement {
    constructor() {
      super();

      this.slides = [];
      this.validSlides = [];
      this.activeSlide = null;
      this.activeIndex = 0;
      this.autoplayInterval = null;
      this.countdownInterval = null;
      this.visibilityTimeout = null;
      this.cartRequest = null;
      this.fetchController = null;
      this.motionQuery = null;
      this.isInitialized = false;
      this.isHoverPaused = false;
      this.isFocusPaused = false;
      this.isEditorPaused = false;
      this.isDragPaused = false;
      this.pointerId = null;
      this.pointerStartX = 0;
      this.pointerStartY = 0;
      this.pointerDeltaX = 0;
      this.isDragging = false;
      this.draggedSlide = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
      this.handleSlideTransitionEnd = this.handleSlideTransitionEnd.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handlePointerCancel = this.handlePointerCancel.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.cacheElements();
      this.readConfiguration();
      this.bindEvents();
      this.refreshValidSlides();
      this.checkExistingCoupons();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.stopCountdown();
      this.clearVisibilityTimeout();
      this.unbindEvents();

      if (this.fetchController) {
        this.fetchController.abort();
      }

      this.resetPointerGesture();
      this.slides.forEach((slide) => slide.classList.remove('is-leaving'));
      this.cartRequest = null;
      this.fetchController = null;
      this.activeSlide = null;
      this.slides = [];
      this.validSlides = [];
      this.isInitialized = false;
    }

    cacheElements() {
      this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
      this.couponButtons = Array.from(this.querySelectorAll('[data-coupon-button]'));
      this.i18n = this.parseI18n();
    }

    readConfiguration() {
      const requestedInterval = Number(this.dataset.rotationInterval);

      this.isEnabled = this.dataset.enabled !== 'false';
      this.isDesignMode = this.dataset.designMode === 'true';
      this.isAutoplayEnabled = this.dataset.autoplay !== 'false';
      this.rotationInterval = Number.isFinite(requestedInterval)
        ? Math.max(1000, requestedInterval)
        : 5000;
      this.isHoverPaused = false;
      this.isFocusPaused = false;
      this.isEditorPaused = false;
      this.isDragPaused = false;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.fetchController = new AbortController();
    }

    bindEvents() {
      this.addEventListener('click', this.handleClick);
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      this.addEventListener('shopify:block:select', this.handleBlockSelect);
      this.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
      this.addEventListener('transitionend', this.handleSlideTransitionEnd);
      this.addEventListener('pointerdown', this.handlePointerDown);
      this.addEventListener('pointermove', this.handlePointerMove);
      this.addEventListener('pointerup', this.handlePointerUp);
      this.addEventListener('pointercancel', this.handlePointerCancel);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      if (typeof this.motionQuery.addEventListener === 'function') {
        this.motionQuery.addEventListener('change', this.handleMotionChange);
      } else {
        this.motionQuery.addListener(this.handleMotionChange);
      }
    }

    unbindEvents() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      this.removeEventListener('transitionend', this.handleSlideTransitionEnd);
      this.removeEventListener('pointerdown', this.handlePointerDown);
      this.removeEventListener('pointermove', this.handlePointerMove);
      this.removeEventListener('pointerup', this.handlePointerUp);
      this.removeEventListener('pointercancel', this.handlePointerCancel);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);

      if (!this.motionQuery) return;

      if (typeof this.motionQuery.removeEventListener === 'function') {
        this.motionQuery.removeEventListener('change', this.handleMotionChange);
      } else {
        this.motionQuery.removeListener(this.handleMotionChange);
      }
    }

    refreshValidSlides() {
      this.clearVisibilityTimeout();

      if (!this.isEnabled && !this.isDesignMode) {
        this.hidden = true;
        this.stopAutoplay();
        this.stopCountdown();
        return;
      }

      const now = Date.now();
      const futureBoundaries = [];

      this.validSlides = this.slides.filter((slide) => {
        const startDate = this.parseDateValue(slide.dataset.promotionStart);
        const endDate = this.parseDateValue(slide.dataset.promotionEnd);
        const isWithinStart = !startDate || now >= startDate;
        const isWithinEnd = !endDate || now < endDate;
        const isValid = isWithinStart && isWithinEnd;

        if (startDate && startDate > now) futureBoundaries.push(startDate);
        if (endDate && endDate > now) futureBoundaries.push(endDate);

        slide.hidden = this.isDesignMode ? false : !isValid;
        return this.isDesignMode || isValid;
      });

      this.scheduleVisibilityUpdate(futureBoundaries);

      if (this.validSlides.length === 0) {
        this.hidden = !this.isDesignMode;
        this.activeSlide = null;
        this.stopAutoplay();
        this.stopCountdown();
        return;
      }

      this.hidden = false;
      this.validSlides.forEach((slide, index) => {
        slide.setAttribute('aria-label', `${index + 1} de ${this.validSlides.length}`);
      });

      let nextIndex = this.validSlides.indexOf(this.activeSlide);

      if (nextIndex < 0 && this.activeSlide) {
        const previousPosition = this.slides.indexOf(this.activeSlide);
        const nextSlide = this.validSlides.find((slide) => this.slides.indexOf(slide) > previousPosition);
        nextIndex = nextSlide ? this.validSlides.indexOf(nextSlide) : 0;
      }

      this.showSlide(nextIndex >= 0 ? nextIndex : 0);
      this.syncAutoplay();
    }

    showSlide(index) {
      if (this.validSlides.length === 0) return;

      const normalizedIndex = ((index % this.validSlides.length) + this.validSlides.length) % this.validSlides.length;
      const nextSlide = this.validSlides[normalizedIndex];
      const previousSlide = this.activeSlide;
      const shouldAnimate = Boolean(
        previousSlide &&
        previousSlide !== nextSlide &&
        !previousSlide.hidden &&
        !this.motionQuery.matches
      );

      this.slides.forEach((slide) => {
        const isActive = slide === nextSlide;

        if (slide !== previousSlide) {
          slide.classList.remove('is-leaving');
        }

        if (!isActive) {
          slide.classList.remove('is-active');
        }

        slide.setAttribute('aria-hidden', String(!isActive));
        slide.inert = !isActive;
      });

      if (previousSlide && previousSlide !== nextSlide) {
        previousSlide.classList.toggle('is-leaving', shouldAnimate);
      }

      if (shouldAnimate) {
        nextSlide.getBoundingClientRect();
      }

      nextSlide.classList.add('is-active');

      this.activeIndex = normalizedIndex;
      this.activeSlide = nextSlide;
      this.startCountdown();
    }

    nextSlide() {
      if (this.validSlides.length < 2) return;
      this.showSlide(this.activeIndex + 1);
    }

    previousSlide() {
      if (this.validSlides.length < 2) return;
      this.showSlide(this.activeIndex - 1);
    }

    syncAutoplay() {
      const shouldAutoplay =
        this.isAutoplayEnabled &&
        this.validSlides.length > 1 &&
        !this.isHoverPaused &&
        !this.isFocusPaused &&
        !this.isEditorPaused &&
        !this.isDragPaused &&
        !this.motionQuery.matches &&
        !document.hidden;

      if (shouldAutoplay) {
        this.startAutoplay();
      } else {
        this.stopAutoplay();
      }
    }

    startAutoplay() {
      if (this.autoplayInterval) return;

      this.autoplayInterval = window.setInterval(() => {
        this.nextSlide();
      }, this.rotationInterval);
    }

    stopAutoplay() {
      if (!this.autoplayInterval) return;

      window.clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }

    startCountdown() {
      this.stopCountdown();

      if (!this.activeSlide || this.activeSlide.dataset.showCountdown !== 'true') return;

      const countdown = this.activeSlide.querySelector('[data-countdown]');
      const endDate = this.parseDateValue(this.activeSlide.dataset.promotionEnd);

      if (!countdown || !endDate) return;

      const update = () => this.updateCountdown(countdown, endDate);
      update();

      if (endDate > Date.now()) {
        this.countdownInterval = window.setInterval(update, 1000);
      }
    }

    updateCountdown(countdown, endDate) {
      const distance = Math.max(0, endDate - Date.now());
      const day = 24 * 60 * 60 * 1000;
      const hour = 60 * 60 * 1000;
      const minute = 60 * 1000;

      this.updateText(countdown.querySelector('[data-countdown-days]'), this.formatTime(Math.floor(distance / day)));
      this.updateText(
        countdown.querySelector('[data-countdown-hours]'),
        this.formatTime(Math.floor((distance % day) / hour))
      );
      this.updateText(
        countdown.querySelector('[data-countdown-minutes]'),
        this.formatTime(Math.floor((distance % hour) / minute))
      );
      this.updateText(
        countdown.querySelector('[data-countdown-seconds]'),
        this.formatTime(Math.floor((distance % minute) / 1000))
      );

      if (distance > 0) return;

      this.stopCountdown();

      if (!this.isDesignMode) {
        this.refreshValidSlides();
      }
    }

    stopCountdown() {
      if (!this.countdownInterval) return;

      window.clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }

    scheduleVisibilityUpdate(boundaries) {
      if (this.isDesignMode || boundaries.length === 0) return;

      const nextBoundary = Math.min(...boundaries);
      const delay = Math.max(0, nextBoundary - Date.now() + 50);

      this.visibilityTimeout = window.setTimeout(() => {
        this.visibilityTimeout = null;
        this.refreshValidSlides();
      }, Math.min(delay, MAX_TIMEOUT));
    }

    clearVisibilityTimeout() {
      if (!this.visibilityTimeout) return;

      window.clearTimeout(this.visibilityTimeout);
      this.visibilityTimeout = null;
    }

    handleMouseEnter() {
      this.isHoverPaused = true;
      this.syncAutoplay();
    }

    handleMouseLeave() {
      this.isHoverPaused = false;
      this.syncAutoplay();
    }

    handleFocusIn() {
      this.isFocusPaused = true;
      this.syncAutoplay();
    }

    handleFocusOut(event) {
      if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;

      this.isFocusPaused = false;
      this.syncAutoplay();
    }

    handleMotionChange() {
      if (this.motionQuery.matches) {
        this.slides.forEach((slide) => slide.classList.remove('is-leaving'));
      }

      this.syncAutoplay();
    }

    handleSlideTransitionEnd(event) {
      if (
        event.propertyName === 'transform' &&
        event.target instanceof Element &&
        event.target.matches('[data-announcement-slide]')
      ) {
        event.target.classList.remove('is-leaving');
      }
    }

    handlePointerDown(event) {
      const isInteractiveTarget = event.target instanceof Element &&
        event.target.closest('a, button, input, select, textarea, [role="button"]');

      if (
        this.validSlides.length < 2 ||
        isInteractiveTarget ||
        (event.pointerType === 'mouse' && event.button !== 0)
      ) {
        return;
      }

      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
      this.pointerDeltaX = 0;
      this.isDragging = false;
      this.isDragPaused = true;
      this.draggedSlide = this.activeSlide;
      this.setPointerCapture(event.pointerId);
      this.syncAutoplay();
    }

    handlePointerMove(event) {
      if (event.pointerId !== this.pointerId || !this.draggedSlide) return;

      const deltaX = event.clientX - this.pointerStartX;
      const deltaY = event.clientY - this.pointerStartY;

      if (!this.isDragging) {
        if (Math.abs(deltaX) < 6) return;

        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          this.finishPointerGesture(false);
          return;
        }

        this.isDragging = true;
        this.classList.add('is-dragging');
      }

      event.preventDefault();

      const viewportWidth = this.clientWidth || 1;
      const maximumDistance = Math.min(180, viewportWidth * 0.4);
      this.pointerDeltaX = Math.max(-maximumDistance, Math.min(maximumDistance, deltaX));
      this.draggedSlide.style.transform = `translate3d(${this.pointerDeltaX}px, 0, 0)`;
    }

    handlePointerUp(event) {
      if (event.pointerId !== this.pointerId) return;
      this.finishPointerGesture(this.isDragging);
    }

    handlePointerCancel(event) {
      if (event.pointerId !== this.pointerId) return;
      this.finishPointerGesture(false);
    }

    finishPointerGesture(allowNavigation) {
      const pointerId = this.pointerId;
      const draggedSlide = this.draggedSlide;
      const dragDistance = this.pointerDeltaX;
      const shouldNavigate = allowNavigation && Math.abs(dragDistance) >= 40;

      if (pointerId !== null && this.hasPointerCapture(pointerId)) {
        this.releasePointerCapture(pointerId);
      }

      this.classList.remove('is-dragging');

      if (draggedSlide) {
        draggedSlide.getBoundingClientRect();
        draggedSlide.style.removeProperty('transform');
      }

      this.pointerId = null;
      this.pointerDeltaX = 0;
      this.isDragging = false;
      this.draggedSlide = null;
      this.isDragPaused = false;

      if (shouldNavigate) {
        if (dragDistance < 0) {
          this.nextSlide();
        } else {
          this.previousSlide();
        }
      }

      this.syncAutoplay();
    }

    resetPointerGesture() {
      this.classList.remove('is-dragging');

      if (this.draggedSlide) {
        this.draggedSlide.style.removeProperty('transform');
      }

      this.pointerId = null;
      this.pointerDeltaX = 0;
      this.isDragging = false;
      this.draggedSlide = null;
      this.isDragPaused = false;
    }

    handleVisibilityChange() {
      this.syncAutoplay();
    }

    handleBlockSelect(event) {
      const slide = event.target instanceof Element
        ? event.target.closest('[data-announcement-slide]')
        : null;

      if (!slide || !this.contains(slide)) return;

      const slideIndex = this.validSlides.indexOf(slide);
      this.isEditorPaused = true;

      if (slideIndex >= 0) {
        this.showSlide(slideIndex);
      }

      this.syncAutoplay();
    }

    handleBlockDeselect(event) {
      const slide = event.target instanceof Element
        ? event.target.closest('[data-announcement-slide]')
        : null;

      if (!slide || !this.contains(slide)) return;

      this.isEditorPaused = false;
      this.syncAutoplay();
    }

    handleClick(event) {
      const button = event.target instanceof Element
        ? event.target.closest('[data-coupon-button]')
        : null;

      if (!button || !this.contains(button) || button.dataset.couponStatus === 'applied') return;
      this.applyCoupon(button);
    }

    async checkExistingCoupons() {
      if (this.couponButtons.length === 0) return;

      try {
        const cart = await this.fetchCart();

        this.couponButtons.forEach((button) => {
          const couponCode = button.dataset.couponCode || '';
          const vendor = button.dataset.vendor || '';

          if (this.isCouponApplied(cart, couponCode) && this.cartHasVendor(cart, vendor)) {
            this.setCouponStatus(button, 'applied');
          }
        });
      } catch (error) {
        // The bar remains usable when the initial cart request is unavailable.
      }
    }

    async applyCoupon(button) {
      const couponCode = (button.dataset.couponCode || '').trim();
      const vendor = button.dataset.vendor || '';

      if (!couponCode) return;

      button.disabled = true;

      try {
        const cart = await this.fetchCart();

        if (!this.cartHasVendor(cart, vendor)) {
          this.setCouponStatus(button, 'not-applied');
          return;
        }

        const existingDiscounts = this.getExistingDiscountCodes(cart);

        if (this.isCouponApplied(cart, couponCode)) {
          this.setCouponStatus(button, 'applied');
          return;
        }

        const response = await fetch(this.getCartUpdateUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            discount: [...existingDiscounts, couponCode].join(',')
          }),
          signal: this.fetchController.signal
        });

        if (!response.ok) throw new Error('Failed to update cart');

        const data = await response.json();
        const applied = (data.discount_codes || []).some((discount) => {
          return this.normalizeCode(discount.code) === this.normalizeCode(couponCode) && discount.applicable === true;
        });

        if (applied) {
          this.setCouponStatus(button, 'applied');
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              detail: { source: ELEMENT_NAME, cart: data }
            })
          );
        } else {
          this.setCouponStatus(button, 'not-applied');
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.setCouponStatus(button, 'not-applied');
        }
      } finally {
        if (button.dataset.couponStatus !== 'applied') {
          button.disabled = false;
        }
      }
    }

    fetchCart() {
      if (this.cartRequest) return this.cartRequest;

      let pendingRequest;

      pendingRequest = fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        signal: this.fetchController.signal
      })
        .then((response) => {
          if (!response.ok) throw new Error('Failed to fetch cart');
          return response.json();
        })
        .finally(() => {
          if (this.cartRequest === pendingRequest) {
            this.cartRequest = null;
          }
        });

      this.cartRequest = pendingRequest;
      return pendingRequest;
    }

    cartHasVendor(cart, vendorName) {
      const normalizedVendor = String(vendorName || '').trim().toLowerCase();
      if (!normalizedVendor) return true;

      return (cart.items || []).some((item) => {
        return String(item.vendor || '').trim().toLowerCase() === normalizedVendor;
      });
    }

    getExistingDiscountCodes(cart) {
      return (cart.cart_level_discount_applications || [])
        .filter((discount) => discount.type === 'discount_code' && discount.title)
        .map((discount) => discount.title);
    }

    isCouponApplied(cart, couponCode) {
      const normalizedCoupon = this.normalizeCode(couponCode);
      return this.getExistingDiscountCodes(cart).some((code) => this.normalizeCode(code) === normalizedCoupon);
    }

    setCouponStatus(button, status) {
      const label = button.querySelector('[data-coupon-label]');
      const isApplied = status === 'applied';

      button.dataset.couponStatus = status;
      button.classList.toggle('is-applied', isApplied);
      button.classList.toggle('is-not-applied', !isApplied);
      button.disabled = isApplied;

      this.updateText(label, isApplied ? this.i18n.couponApplied : this.i18n.couponNotApplied);
    }

    getCartUpdateUrl() {
      if (window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url) {
        return window.Theme.routes.cart_update_url;
      }

      return '/cart/update.js';
    }

    parseI18n() {
      const defaults = {
        couponApplied: 'CUPOM APLICADO',
        couponNotApplied: 'CUPOM NÃO APLICADO'
      };
      const element = this.querySelector('[data-unilar-announcement-i18n]');

      if (!element || !element.textContent.trim()) return defaults;

      try {
        return { ...defaults, ...JSON.parse(element.textContent) };
      } catch (error) {
        return defaults;
      }
    }

    parseDateValue(value) {
      if (!value || !String(value).trim()) return null;

      const normalized = String(value).trim().replace(' ', 'T');
      const parsed = new Date(normalized).getTime();

      return Number.isNaN(parsed) ? null : parsed;
    }

    formatTime(value) {
      return String(value).padStart(2, '0');
    }

    normalizeCode(value) {
      return String(value || '').trim().toUpperCase();
    }

    updateText(element, value) {
      const nextValue = String(value);

      if (element && element.textContent !== nextValue) {
        element.textContent = nextValue;
      }
    }
  }

  customElements.define(ELEMENT_NAME, UnilarAnnouncementBar);
})();
